import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CustomerType, EntityLegalFormGroup } from '../../../resources/base/models/enums';
import {
  ExecutiveInfoDataFormType,
  SignatoryInfoDataFormType,
} from '../../../screens/FormPage/Step4/businessCustomerDetailsForms/initialValues';
import { getIsSoleTrader } from '../../common/miscUtils';
import { Optional } from '../../common/miscUtils.types';

/**
 * - Data currently missing in either/or BFT and Miles that is not provided to FE
 * preventing proper state restoration for the quote (and customer related to that quote).
 * - Ultimately to be removed.
 */
const missingMilesData = {
  siren: '' as string | undefined,
  hasCoCustomer: false,
  milesCoCustomerRelationType: '' as QuoteSchema.Schemas.MilesCoCustomerRelationType,
  legalEntity: '' as QuoteSchema.Schemas.EntityLegalFormType | undefined,
  productName: undefined as undefined | string /* e.g. LLDN_PR */,
  posFrOccupation: undefined as Optional<CreditIntegration.Schemas.OccupationEnumValue>,
  posFrOccupationCoCustomer: undefined as Optional<CreditIntegration.Schemas.OccupationEnumValue>,
};

export type StakeholderContactDefinition =
  | {
      role: Extract<CustomerIntegration.Schemas.StakeHolderContactType, 'DIRECTOR'>;
      milesStakeholderContactId?: string;
      signatoryStakeholderContactId?: string;
      milesContactId?: string;
      milesEmailId?: string;
      milesMobilePhoneNumberId?: string;
      formData: ExecutiveInfoDataFormType;
    }
  | {
      role: Extract<CustomerIntegration.Schemas.StakeHolderContactType, 'SIGNATORY'>;
      milesStakeholderContactId?: string;
      milesContactId: string;
      signatoryStakeholderContactId?: string;
      milesEmailId?: string;
      milesMobilePhoneNumberId?: string;
      formData: ExecutiveInfoDataFormType | SignatoryInfoDataFormType;
    };

export type MissingMilesData = typeof missingMilesData;

type StakeholderContactRecords = {
  [StakeholderKey in CustomerIntegration.Schemas.StakeHolderContactType]: StakeholderContactDefinition | undefined;
};

const initialState = {
  milesCustomerId: '',
  milesCustomerIdCoCustomer: '',
  /**
   * @see {@link missingMilesData}
   */
  missingMilesData,
  customerType: undefined as CustomerType | undefined,
  availableSignatoryStakeholders: [] as Array<StakeholderContactDefinition>,
  stakeholderContacts: {} as StakeholderContactRecords,
  isSignatoryFormActive: false,
  shouldLinkDirectorStakeholder: false,
  shouldLinkSignatoryStakeholder: false,
};

export type CustomerState = typeof initialState;
export type CustomerData = CustomerIntegration.Schemas.GetCustomerByIdResponse['customerData'];

export const customerSlice = createSlice({
  name: 'customer',
  initialState,
  selectors: {
    selectCustomerId: (state: CustomerState) => {
      return state.milesCustomerId;
    },
    selectCoCustomerId: (state: CustomerState) => {
      return state.milesCustomerIdCoCustomer;
    },
    selectMissingMilesData: (state: CustomerState) => {
      return state.missingMilesData;
    },
    selectCustomerType: (state: CustomerState) => {
      return state.customerType;
    },
    selectLegalEntity: (state: CustomerState) => {
      return state.missingMilesData.legalEntity;
    },
    selectHasCoCustomer: (state: CustomerState) => {
      return state.missingMilesData.hasCoCustomer;
    },
    selectPosFrOccupationFromCustomer: (state: CustomerState) => {
      return state.missingMilesData.posFrOccupation;
    },
    selectPosFrOccupationCoCustomerFromCustomer: (state: CustomerState) => {
      return state.missingMilesData.posFrOccupationCoCustomer;
    },
    selectStakeholderContacts: (state: CustomerState) => {
      return state.stakeholderContacts;
    },
    selectAvailableSignatories: (state: CustomerState) => {
      return state.availableSignatoryStakeholders;
    },
    selectIsSignatoryFormActive: (state: CustomerState) => {
      return state.isSignatoryFormActive;
    },
    selectShouldLinkDirectorStakeholder: (state: CustomerState) => {
      return state.shouldLinkDirectorStakeholder;
    },
    selectShouldLinkSignatoryStakeholder: (state: CustomerState) => {
      return state.shouldLinkSignatoryStakeholder;
    },
    selectExistingCustomerEntityLegalFormGroup: (state: CustomerState) => {
      if (state.missingMilesData.legalEntity) {
        return getIsSoleTrader(state.missingMilesData.legalEntity)
          ? EntityLegalFormGroup.SoleTrader
          : EntityLegalFormGroup.NonSoleTrader;
      }
      return EntityLegalFormGroup.None;
    },
  },
  reducers: {
    setCurrentCustomerId(state, { payload }: PayloadAction<string>) {
      state.milesCustomerId = payload;
    },
    setCurrentCoCustomerId(state, { payload }: PayloadAction<string>) {
      state.milesCustomerIdCoCustomer = payload;
    },
    setMissingMilesData(state, { payload }: PayloadAction<Partial<MissingMilesData>>) {
      state.missingMilesData = {
        ...state.missingMilesData,
        ...payload,
      };
    },
    setCustomerType(state, { payload }: PayloadAction<CustomerType | undefined>) {
      state.customerType = payload;
    },
    resetCustomer() {
      return initialState;
    },
    setCustomerInitialState(_, { payload }: PayloadAction<CustomerState>) {
      return {
        ...payload,
      };
    },
    setAvailableSignatories(state, { payload }: PayloadAction<StakeholderContactDefinition[]>) {
      state.availableSignatoryStakeholders = payload;
    },
    setHasCoCustomer(state, { payload }: PayloadAction<boolean>) {
      state.missingMilesData.hasCoCustomer = payload;
    },
    setStakeholderContact(state, { payload }: PayloadAction<StakeholderContactDefinition>) {
      state.stakeholderContacts[payload.role] = payload;
    },
    removeAllStakeholderContacts(state) {
      state.stakeholderContacts = {} as StakeholderContactRecords;
    },
    setRemoveStakeholderContact(state, { payload }: PayloadAction<StakeholderContactDefinition>) {
      const newState = { ...state.stakeholderContacts };
      delete newState[payload.role];
      state.stakeholderContacts = newState;
    },
    setIsSignatoryFormActive(state, { payload }: PayloadAction<boolean>) {
      state.isSignatoryFormActive = payload;
    },
    setShouldLinkDirectorStakeholder(state, { payload }: PayloadAction<boolean>) {
      state.shouldLinkDirectorStakeholder = payload;
    },
    setShouldLinkSignatoryStakeholder(state, { payload }: PayloadAction<boolean>) {
      state.shouldLinkSignatoryStakeholder = payload;
    },
  },
});

export const customerSelectors = customerSlice.selectors;
export const {
  setCurrentCoCustomerId,
  setCurrentCustomerId,
  setCustomerInitialState,
  setCustomerType,
  setMissingMilesData,
  setStakeholderContact,
  removeAllStakeholderContacts,
  setRemoveStakeholderContact,
  setAvailableSignatories,
  resetCustomer,
  setIsSignatoryFormActive,
  setShouldLinkDirectorStakeholder,
  setShouldLinkSignatoryStakeholder,
  setHasCoCustomer,
} = customerSlice.actions;
export const customerReducer = customerSlice.reducer;
